import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { GET_CARGO_REVIEW, RECOMMEND_CARGO_DECISION } from '@modules/applications/private-transit-cargo/private-transit-cargo-review.graphql';
import { CargoReviewRequest } from '@modules/applications/private-transit-cargo/private-transit-cargo-review.models';
import { GetCargoReviewActions, RecommendCargoDecisionActions } from './private-transit-cargo.actions';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  ACKNOWLEDGE_PRIVATE_TRANSIT_CARGO_CONDITIONS,
  GET_PRIVATE_TRANSIT_CARGO_REQUEST,
  LIST_PRIVATE_TRANSIT_CARGO_REQUESTS,
  RESUBMIT_PRIVATE_TRANSIT_CARGO_FOR_APPROVAL,
  SAVE_PRIVATE_TRANSIT_CARGO_REQUEST,
  SUBMIT_PRIVATE_TRANSIT_CARGO_REQUEST,
  UPDATE_PRIVATE_TRANSIT_CARGO_REQUEST,
  WITHDRAW_PRIVATE_TRANSIT_CARGO_REQUEST,
} from '../graphql/private-transit-cargo.graphql';
import {
  AcknowledgePrivateTransitCargoConditionsActions,
  GetPrivateTransitCargoRequestActions,
  ListPrivateTransitCargoRequestsActions,
  ResubmitPrivateTransitCargoForApprovalActions,
  SavePrivateTransitCargoRequestActions,
  SubmitPrivateTransitCargoRequestActions,
  UpdatePrivateTransitCargoRequestActions,
  WithdrawPrivateTransitCargoRequestActions,
} from './private-transit-cargo.actions';
import {
  AcknowledgePrivateTransitCargoConditionsMutationData,
  PrivateTransitCargoGqlResponse,
  PrivateTransitCargoMutationData,
  PrivateTransitCargoPageResponse,
  PrivateTransitCargoRequest,
  WithdrawPrivateTransitCargoMutationData,
} from '../models/private-transit-cargo.models';

@Injectable()
export class PrivateTransitCargoEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);
  private readonly swalService = inject(SwalService);

  review$ = createEffect(() => this.actions$.pipe(
    ofType(GetCargoReviewActions.request),
    switchMap(({ request }) => this.graphqlService.query<{
      getPrivateTransitCargoRequest: PrivateTransitCargoGqlResponse<CargoReviewRequest>;
    }>({
      query: GET_CARGO_REVIEW,
      apolloNamespace: ApolloNamespace.registration,
      variables: { requestUuid: request },
    }).pipe(
      map(({ data, error }) => {
        const response = data?.getPrivateTransitCargoRequest;

        return response?.status && response.data && !error
          ? GetCargoReviewActions.success({ response })
          : GetCargoReviewActions.failure({ error: error?.message || response?.message || 'Unable to load request.' });
      }),
      catchError((error) => of(GetCargoReviewActions.failure({ error }))),
    )),
  ));

  recommendation$ = createEffect(() => this.actions$.pipe(
    ofType(RecommendCargoDecisionActions.request),
    exhaustMap(({ request }) => this.graphqlService.mutate<{
      recommendPrivateTransitCargoDecision: PrivateTransitCargoGqlResponse<{ uuid: string }>;
    }>({
      mutation: RECOMMEND_CARGO_DECISION,
      apolloNamespace: ApolloNamespace.registration,
      variables: { input: request },
    }).pipe(
      map(({ data }) => {
        const response = data?.recommendPrivateTransitCargoDecision;

        return response?.status && response.data
          ? RecommendCargoDecisionActions.success({ response })
          : RecommendCargoDecisionActions.failure({ error: response?.message || 'Unable to record recommendation.' });
      }),
      catchError((error) => of(RecommendCargoDecisionActions.failure({ error }))),
    )),
  ));

  listRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListPrivateTransitCargoRequestsActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .query<{ listPrivateTransitCargoRequests: { content: PrivateTransitCargoRequest[] } | PrivateTransitCargoPageResponse }>({
            query: LIST_PRIVATE_TRANSIT_CARGO_REQUESTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request.pageableParam ?? null,
              vesselNominationUuid: request.vesselNominationUuid ?? null,
              supplierCompanyUuid: request.supplierCompanyUuid ?? null,
              statuses: request.statuses ?? null,
            },
          })
          .pipe(
            map((response) => {
              if (response.error) {
                throw response.error;
              }

              const resData = response.data?.listPrivateTransitCargoRequests;
              const content = resData?.content ?? [];
              const pageResponse: PrivateTransitCargoPageResponse = {
                content,
                first: (resData as PrivateTransitCargoPageResponse)?.first ?? true,
                hasContent: content.length > 0,
                hasNext: (resData as PrivateTransitCargoPageResponse)?.hasNext ?? false,
                hasPrevious: (resData as PrivateTransitCargoPageResponse)?.hasPrevious ?? false,
                last: (resData as PrivateTransitCargoPageResponse)?.last ?? true,
                number: (resData as PrivateTransitCargoPageResponse)?.number ?? 0,
                numberOfElements: (resData as PrivateTransitCargoPageResponse)?.numberOfElements ?? content.length,
                size: (resData as PrivateTransitCargoPageResponse)?.size ?? 10,
                totalElements: (resData as PrivateTransitCargoPageResponse)?.totalElements ?? content.length,
                totalPages: (resData as PrivateTransitCargoPageResponse)?.totalPages ?? 1,
              };
              return ListPrivateTransitCargoRequestsActions.success({ response: pageResponse });
            }),
            catchError((error) =>
              of(ListPrivateTransitCargoRequestsActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  getRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetPrivateTransitCargoRequestActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .query<{ getPrivateTransitCargoRequest: PrivateTransitCargoGqlResponse<PrivateTransitCargoRequest> }>({
            query: GET_PRIVATE_TRANSIT_CARGO_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { requestUuid: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.getPrivateTransitCargoRequest;
              if (res?.status) {
                return GetPrivateTransitCargoRequestActions.success({ response: res });
              }
              return GetPrivateTransitCargoRequestActions.failure({
                error: res?.message ?? 'Failed to retrieve private transit cargo request',
              });
            }),
            catchError((error) =>
              of(GetPrivateTransitCargoRequestActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  saveDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SavePrivateTransitCargoRequestActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ savePrivateTransitCargoRequest: PrivateTransitCargoGqlResponse<PrivateTransitCargoMutationData> }>({
            mutation: SAVE_PRIVATE_TRANSIT_CARGO_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.savePrivateTransitCargoRequest;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Private transit cargo draft saved successfully',
                );
                return SavePrivateTransitCargoRequestActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to save private transit cargo draft',
              );
              return SavePrivateTransitCargoRequestActions.failure({
                error: res?.message ?? 'Failed to save draft',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to save private transit cargo draft',
              );
              return of(SavePrivateTransitCargoRequestActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  updateDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdatePrivateTransitCargoRequestActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ updatePrivateTransitCargoRequest: PrivateTransitCargoGqlResponse<PrivateTransitCargoMutationData> }>({
            mutation: UPDATE_PRIVATE_TRANSIT_CARGO_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.updatePrivateTransitCargoRequest;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Private transit cargo updated successfully',
                );
                return UpdatePrivateTransitCargoRequestActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to update private transit cargo',
              );
              return UpdatePrivateTransitCargoRequestActions.failure({
                error: res?.message ?? 'Failed to update draft',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to update private transit cargo',
              );
              return of(UpdatePrivateTransitCargoRequestActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  submitRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmitPrivateTransitCargoRequestActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ submitPrivateTransitCargoRequest: PrivateTransitCargoGqlResponse<PrivateTransitCargoMutationData> }>({
            mutation: SUBMIT_PRIVATE_TRANSIT_CARGO_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.submitPrivateTransitCargoRequest;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Private transit cargo request submitted successfully',
                );
                return SubmitPrivateTransitCargoRequestActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to submit private transit cargo request',
              );
              return SubmitPrivateTransitCargoRequestActions.failure({
                error: res?.message ?? 'Failed to submit request',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to submit private transit cargo request',
              );
              return of(SubmitPrivateTransitCargoRequestActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  resubmitRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResubmitPrivateTransitCargoForApprovalActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{
            resubmitPrivateTransitCargoForApproval: PrivateTransitCargoGqlResponse<PrivateTransitCargoMutationData>;
          }>({
            mutation: RESUBMIT_PRIVATE_TRANSIT_CARGO_FOR_APPROVAL,
            apolloNamespace: ApolloNamespace.registration,
            variables: { requestUuid: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.resubmitPrivateTransitCargoForApproval;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Private transit cargo re-submitted for approval successfully',
                );
                return ResubmitPrivateTransitCargoForApprovalActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to re-submit private transit cargo for approval',
              );
              return ResubmitPrivateTransitCargoForApprovalActions.failure({
                error: res?.message ?? 'Failed to re-submit request',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to re-submit private transit cargo for approval',
              );
              return of(ResubmitPrivateTransitCargoForApprovalActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  withdrawRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WithdrawPrivateTransitCargoRequestActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ withdrawPrivateTransitCargoRequest: PrivateTransitCargoGqlResponse<WithdrawPrivateTransitCargoMutationData> }>({
            mutation: WITHDRAW_PRIVATE_TRANSIT_CARGO_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: {
                requestUuid: request.requestUuid,
                reason: request.reason?.trim() || '',
              },
            },
          })
          .pipe(
            map((response) => {
              const res = response.data?.withdrawPrivateTransitCargoRequest;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Private transit cargo request withdrawn successfully',
                );
                return WithdrawPrivateTransitCargoRequestActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to withdraw private transit cargo request',
              );
              return WithdrawPrivateTransitCargoRequestActions.failure({
                error: res?.message ?? 'Failed to withdraw request',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to withdraw private transit cargo request',
              );
              return of(WithdrawPrivateTransitCargoRequestActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  acknowledgeConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AcknowledgePrivateTransitCargoConditionsActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{
            acknowledgePrivateTransitCargoConditions: PrivateTransitCargoGqlResponse<AcknowledgePrivateTransitCargoConditionsMutationData>;
          }>({
            mutation: ACKNOWLEDGE_PRIVATE_TRANSIT_CARGO_CONDITIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: {
                accept: request.accept,
                reason: request.reason ?? null,
                requestUuid: request.requestUuid,
              },
            },
          })
          .pipe(
            map((response) => {
              const res = response.data?.acknowledgePrivateTransitCargoConditions;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Conditions acknowledged successfully',
                );
                return AcknowledgePrivateTransitCargoConditionsActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to acknowledge conditions',
              );
              return AcknowledgePrivateTransitCargoConditionsActions.failure({
                error: res?.message ?? 'Failed to acknowledge conditions',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to acknowledge conditions',
              );
              return of(AcknowledgePrivateTransitCargoConditionsActions.failure({ error }));
            }),
          ),
      ),
    ),
  );
}

